import { CheckmarkFilled, Close } from "@carbon/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ReactNode, useCallback, useMemo } from "react";
import { z } from "zod";

import { companyMembersQueryOptions } from "@/api/companyMembers";
import { formSummariesQueryOptions } from "@/api/formSummaries";
import { listCampaignsQueryOptions } from "@/api/listCampaigns";
import { CampaignCard, CampaignDetailsLink } from "@/components/admin/campaignCard";
import { CampaignCardSkeleton } from "@/components/admin/campaignCardSkeleton";
import {
  activeFilterCount,
  CampaignFilters,
  CampaignFilterValues,
} from "@/components/admin/campaignFilters";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { buttonVariants } from "@/components/input/button";
import { Item } from "@/components/input/combobox";
import { CampaignGroupsState, PENDING_CAMPAIGN_GROUPS, useCampaignGroups } from "@/hooks/useCampaignGroups";
import { Campaign } from "@/schemas/campaign";
import { EmployeeIdSchema } from "@/schemas/employeeId";
import { IsoDateSchema, lenient } from "@/util/lenient";

// Filters live in the URL so they survive opening an evaluation and coming
// back, reloads and shared links (specs/002-campaigns-mobile-draft FR-018).

const CampaignsSearchSchema = z.object({
  // Name of the evaluation just created by the wizard, for the success banner.
  created: lenient(z.string()),
  q: lenient(z.string()),
  member: lenient(EmployeeIdSchema),
  from: lenient(IsoDateSchema),
  to: lenient(IsoDateSchema),
  // Only "Most recent" exists today; the default stays out of the URL.
  sort: lenient(z.enum(["recent"])),
});

export const Route = createFileRoute("/dashboard/_authenticated/admin/campaigns/")({
  component: RouteComponent,
  validateSearch: CampaignsSearchSchema,
  staticData: {
    pageWidth: "wide",
    breadcrumb: ["Results"],
  },
});

const CLEARED_FILTERS: CampaignFilterValues = {
  q: undefined,
  member: undefined,
  from: undefined,
  to: undefined,
};

function RouteComponent() {
  const search = Route.useSearch();
  const { created, member: selectedMemberId, from: fromDate, to: toDate } = search;
  const query = search.q ?? "";
  const navigate = Route.useNavigate();

  const updateFilters = useCallback(
    (patch: CampaignFilterValues) => {
      navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true });
    },
    [navigate],
  );
  const clearFilters = useCallback(() => updateFilters(CLEARED_FILTERS), [updateFilters]);

  const { data: campaignsPage, isLoading: isLoadingCampaigns } = useQuery(
    listCampaignsQueryOptions(),
  );
  const { data: membersPage } = useQuery(companyMembersQueryOptions());
  const { data: forms } = useQuery(formSummariesQueryOptions());

  // Every campaign's groups: the member filter below needs all of them, not just the visible ones.
  const campaignGroupsById = useCampaignGroups(
    campaignsPage?.content,
    membersPage?.content,
    forms,
  );

  const memberItems = useMemo<Item[]>(
    () =>
      (membersPage?.content ?? []).map((member) => ({
        value: member.uuid,
        label: member.name,
      })),
    [membersPage?.content],
  );

  // TODO(admin): search/member/date filtering and the "Most recent" sort
  // below all run client-side over the single page returned by
  // `listCampaignsQueryOptions()` (see that file's TODO — the endpoint has
  // no filter/sort/pagination query params yet, even though its
  // `pageableSchema` return type implies pagination is coming). This will
  // silently miss campaigns once the backend actually paginates
  // (`totalPages > 1`) — revisit once server-side filtering/sorting lands.
  const filteredCampaigns = useMemo(() => {
    const content = campaignsPage?.content ?? [];

    return content.filter((campaign) => {
      if (
        query &&
        !campaign.name.toLowerCase().includes(query.toLowerCase())
      ) {
        return false;
      }

      if (selectedMemberId) {
        const members = campaignGroupsById.get(campaign.id)?.members ?? [];
        if (!members.some((member) => member.uuid === selectedMemberId)) {
          return false;
        }
      }

      // Overlap semantics (confirmed): keep the campaign if its period
      // ([startsAt, endsAt]) overlaps the [fromDate, toDate] filter range at
      // all, rather than requiring the campaign to be fully contained
      // within it.
      if (fromDate && campaign.endsAt < fromDate) {
        return false;
      }

      if (toDate && campaign.startsAt > toDate) {
        return false;
      }

      return true;
    });
  }, [
    campaignsPage?.content,
    query,
    selectedMemberId,
    campaignGroupsById,
    fromDate,
    toDate,
  ]);

  const sortedCampaigns = useMemo(
    () =>
      filteredCampaigns.toSorted((a, b) =>
        b.startsAt.localeCompare(a.startsAt),
      ),
    [filteredCampaigns],
  );

  const hasAnyCampaigns = (campaignsPage?.content.length ?? 0) > 0;
  const hasNoCampaignsAtAll = campaignsPage !== undefined && !hasAnyCampaigns;
  const hasNoFilteredResults = hasAnyCampaigns && sortedCampaigns.length === 0;

  return (
    <div className="
      flex flex-col gap-6 pb-6
      lg:gap-8
    ">
      <Breadcrumbs />

      <div
        className="
          flex flex-wrap items-center justify-between gap-4 border-b-2
          border-b-primary-100 pb-6
          lg:pb-8
        "
      >
        <h1 className="text-page-title">Evaluations</h1>
        <Link
          className={buttonVariants({
            intent: "primary",
            className: `
              w-full justify-center
              sm:w-auto
            `,
          })}
          to="/dashboard/admin/campaigns/create"
        >
          Create evaluation
        </Link>
      </div>

      {created && (
        <div
          className="
            flex items-center gap-3 rounded-lg border border-success-100
            bg-success-50 py-1 pr-1 pl-4 text-body3 text-success-300
          "
          role="status"
        >
          <CheckmarkFilled className="shrink-0 text-success-200" size={20} />
          <span className="flex-1 py-2">
            {`Evaluation "${created}" created. Open it from the list when it should start.`}
          </span>
          <button
            aria-label="Dismiss"
            className="
              flex size-11 shrink-0 cursor-pointer items-center justify-center
              rounded-md
              hover:bg-success-100
            "
            onClick={() => navigate({ search: (previous) => ({ ...previous, created: undefined }) })}
            type="button"
          >
            <Close size={16} />
          </button>
        </div>
      )}

      <CampaignFilters
        memberItems={memberItems}
        onChange={updateFilters}
        onClear={clearFilters}
        values={search}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-h3 text-primary-default">Results</div>
        <div className="flex items-center gap-2">
          <label
            className="text-body3 text-neutral-600"
            htmlFor="campaign-sort"
          >
            Sort by
          </label>
          {/*
            Wave 1 offers a single sort mode ("Most recent", applied above
            client-side). It is driven by the `sort` search param so new
            modes only need a schema entry and a comparator.
          */}
          <select
            className="
              min-h-11 rounded-md border border-neutral-400 bg-[canvas] px-3
              text-base outline-none
            "
            id="campaign-sort"
            onChange={(event) =>
              navigate({
                search: (previous) => ({
                  ...previous,
                  sort: event.target.value === "recent" ? undefined : (event.target.value as "recent"),
                }),
                replace: true,
              })
            }
            value={search.sort ?? "recent"}
          >
            <option value="recent">Most recent</option>
          </select>
        </div>
      </div>

      <CampaignsResults
        hasActiveFilters={activeFilterCount(search) > 0}
        hasNoCampaignsAtAll={hasNoCampaignsAtAll}
        hasNoFilteredResults={hasNoFilteredResults}
        isLoading={isLoadingCampaigns}
        groupsByCampaignId={campaignGroupsById}
        onClearFilters={clearFilters}
        sortedCampaigns={sortedCampaigns}
      />
    </div>
  );
}

interface CampaignsResultsProps {
  isLoading: boolean;
  hasNoCampaignsAtAll: boolean;
  hasNoFilteredResults: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  sortedCampaigns: Campaign[];
  groupsByCampaignId: Map<Campaign["id"], CampaignGroupsState>;
}

const RESULTS_GRID = "grid-cards gap-6";

const SKELETON_COUNT = 6;

function CampaignsResults(props: CampaignsResultsProps) {
  if (props.isLoading) {
    return (
      <div className={RESULTS_GRID} role="status">
        <span className="sr-only">Loading evaluations…</span>
        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <CampaignCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (props.hasNoCampaignsAtAll) {
    return (
      <EmptyState text="No evaluations yet.">
        <Link
          className={buttonVariants({ intent: "primary", size: "sm" })}
          to="/dashboard/admin/campaigns/create"
        >
          Create evaluation
        </Link>
      </EmptyState>
    );
  }

  if (props.hasNoFilteredResults) {
    return (
      <EmptyState text="No evaluations match your filters.">
        {props.hasActiveFilters && (
          <button
            className={buttonVariants({ size: "sm", className: "min-h-11" })}
            onClick={props.onClearFilters}
            type="button"
          >
            Clear filters
          </button>
        )}
      </EmptyState>
    );
  }

  return (
    <div className={RESULTS_GRID}>
      {props.sortedCampaigns.map((campaign) => (
        <CampaignCard
          action={<CampaignDetailsLink campaignId={campaign.id} />}
          campaign={campaign}
          groups={props.groupsByCampaignId.get(campaign.id) ?? PENDING_CAMPAIGN_GROUPS}
          key={campaign.id}
        />
      ))}
    </div>
  );
}

function EmptyState({ text, children }: { text: string; children?: ReactNode }) {
  return (
    <div className="
      flex flex-col items-start gap-4 rounded-lg border-2 border-dashed
      border-primary-100 p-6
    ">
      <div className="text-body2 text-neutral-600">{text}</div>
      {children}
    </div>
  );
}
